import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { handleAuthResult, readOAuthFragment, scrubOAuthFragment } from '@cowoo/shared';
import { auth } from '../../lib/auth';
import { afterAuthentication } from './afterAuth';
import { rememberDetour } from './pending';

/**
 * `/auth/callback` (04 § 11.3).
 *
 * The provider hands a one-time temp token back in the URL **fragment**. It is
 * read once and scrubbed immediately: a token in the address bar lands in
 * history, in screenshots and in the referrer.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  // The temp token is single-use, so StrictMode's second mount must not spend it.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = readOAuthFragment(window.location.hash);
    scrubOAuthFragment((url) => window.history.replaceState(null, '', url));

    if (!token) {
      navigate('/login?error=oauth', { replace: true });
      return;
    }

    void (async () => {
      try {
        const state = handleAuthResult(await auth.completeOAuth(token));
        if (state.status === 'totpRequired') {
          rememberDetour({ kind: 'totp_required', token: state.token });
          navigate('/login/totp', { replace: true });
          return;
        }
        if (state.status === 'challengeRequired') {
          rememberDetour({ kind: 'challenge', token: state.token });
          navigate('/login/challenge', { replace: true });
          return;
        }
        if (state.status === 'authenticated') {
          navigate(await afterAuthentication(state), { replace: true });
          return;
        }
        navigate('/login?error=oauth', { replace: true });
      } catch {
        navigate('/login?error=oauth', { replace: true });
      }
    })();
  }, [navigate]);

  return null;
}
